import { useMemo, useState } from 'react';
import { AlertCircle, CheckCircle, Download, Loader2 } from 'lucide-react';
import type { ValidateResult } from '../types';
import type { SkippedImportRow } from '../errorRowsExport';

interface ValidateStepProps {
  loading: boolean;
  result: ValidateResult | null;
  onValidate: () => void;
  onDownloadErrors?: () => void;
  onDownloadSkipped?: () => void;
  validCount?: number;
  skippedRows?: SkippedImportRow[];
}

type LogFilter = 'all' | 'errors' | 'warnings' | 'skipped';

type LogRow = {
  row: number;
  field: string;
  message: string;
  severity: 'error' | 'warning' | 'skipped';
};

export default function ValidateStep({
  loading,
  result,
  onValidate,
  onDownloadErrors,
  onDownloadSkipped,
  validCount,
  skippedRows = [],
}: ValidateStepProps) {
  const [logFilter, setLogFilter] = useState<LogFilter>('all');

  const errorCount = result?.errors?.length || 0;
  const warningCount = result?.warnings?.length || 0;
  const skippedCount = skippedRows.length;
  const canImportValid = (validCount ?? result?.valid_rows ?? 0) > 0;
  const hasLog = errorCount > 0 || warningCount > 0 || skippedCount > 0;

  const logRows = useMemo((): LogRow[] => {
    if (!result && !skippedCount) return [];
    const errors: LogRow[] = (result?.errors || []).map((e) => ({
      row: e.row,
      field: e.field || '',
      message: e.message,
      severity: 'error' as const,
    }));
    const warnings: LogRow[] = (result?.warnings || []).map((e) => ({
      row: e.row,
      field: e.field || '',
      message: e.message,
      severity: 'warning' as const,
    }));
    const skipped: LogRow[] = skippedRows.map((s) => ({
      row: s.row,
      field: '',
      message: s.reason || 'Skipped',
      severity: 'skipped' as const,
    }));
    if (logFilter === 'errors') return errors;
    if (logFilter === 'warnings') return warnings;
    if (logFilter === 'skipped') return skipped;
    return [...errors, ...warnings, ...skipped].sort((a, b) => a.row - b.row);
  }, [result, skippedRows, skippedCount, logFilter]);

  return (
    <div className="space-y-4">
      <div>
        <h4 className="text-lg font-bold text-gray-900 mb-1">Step 4: Validate</h4>
        <p className="text-gray-600 text-sm">
          Run a dry-run against the server. No records are saved until you import.
        </p>
      </div>

      {!result && !loading && (
        <button type="button" onClick={onValidate} className="btn-primary inline-flex items-center gap-2">
          Run Validation
        </button>
      )}

      {loading && (
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2 className="w-10 h-10 text-primary-600 animate-spin mb-3" />
          <p className="text-gray-600 font-medium">Validating…</p>
        </div>
      )}

      {result && !loading && (
        <div className="space-y-4">
          <div
            className={`p-4 rounded-lg flex items-start gap-3 border ${
              result.ready_to_import
                ? 'bg-green-50 border-green-200 text-green-800'
                : canImportValid
                  ? 'bg-amber-50 border-amber-200 text-amber-900'
                  : 'bg-red-50 border-red-200 text-red-800'
            }`}
          >
            {result.ready_to_import ? (
              <CheckCircle className="w-6 h-6 shrink-0 mt-0.5" />
            ) : (
              <AlertCircle className="w-6 h-6 shrink-0 mt-0.5" />
            )}
            <div className="min-w-0">
              <h5 className="font-bold">{result.message}</h5>
              <p className="mt-1 text-sm">
                Valid: {result.valid_rows} / {result.total_rows}
                {skippedCount ? ` · Skipped: ${skippedCount}` : typeof result.skipped === 'number' ? ` · Skipped: ${result.skipped}` : ''}
                {errorCount ? ` · Errors: ${errorCount}` : ''}
                {warningCount ? ` · Warnings: ${warningCount}` : ''}
              </p>
              {skippedCount > 0 && (
                <p className="mt-2 text-sm">
                  Skipped rows were excluded in Review (manual skip or client checks). Open the{' '}
                  <strong>Skipped</strong> tab to see why, or download them.
                </p>
              )}
              {!result.ready_to_import && canImportValid && (
                <p className="mt-2 text-sm">
                  You can download error/skipped rows to fix and re-upload later, or continue and import only the{' '}
                  <strong>{result.valid_rows}</strong> valid record{result.valid_rows === 1 ? '' : 's'}.
                </p>
              )}
              {!result.ready_to_import && !canImportValid && (
                <p className="mt-2 text-sm">
                  No valid rows to import. Download the error/skipped lists, fix the file, then re-upload.
                </p>
              )}
            </div>
          </div>

          {hasLog && (
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-2 justify-between">
                <div className="flex flex-wrap gap-1 rounded-lg bg-gray-100 p-0.5">
                  {(
                    [
                      { id: 'all', label: `All (${errorCount + warningCount + skippedCount})` },
                      { id: 'errors', label: `Errors (${errorCount})` },
                      { id: 'warnings', label: `Warnings (${warningCount})` },
                      { id: 'skipped', label: `Skipped (${skippedCount})` },
                    ] as const
                  ).map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setLogFilter(tab.id)}
                      className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                        logFilter === tab.id
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2">
                  {errorCount > 0 && onDownloadErrors && (
                    <button
                      type="button"
                      onClick={onDownloadErrors}
                      className="btn-secondary text-sm inline-flex items-center gap-1.5"
                    >
                      <Download className="w-4 h-4" />
                      Download errors only
                    </button>
                  )}
                  {skippedCount > 0 && onDownloadSkipped && (
                    <button
                      type="button"
                      onClick={onDownloadSkipped}
                      className="btn-secondary text-sm inline-flex items-center gap-1.5"
                    >
                      <Download className="w-4 h-4" />
                      Download skipped
                    </button>
                  )}
                </div>
              </div>

              <div className="overflow-x-auto border border-gray-200 rounded-lg max-h-64">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold">Row</th>
                      <th className="text-left px-3 py-2 font-semibold">Field</th>
                      <th className="text-left px-3 py-2 font-semibold">Message / Reason</th>
                      <th className="text-left px-3 py-2 font-semibold">Severity</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {logRows.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-3 py-6 text-center text-gray-400">
                          No {logFilter === 'all' ? 'messages' : logFilter} in this list
                        </td>
                      </tr>
                    ) : (
                      logRows.map((err, idx) => (
                        <tr key={`${err.severity}-${err.row}-${err.field}-${idx}`}>
                          <td className="px-3 py-2">{err.row}</td>
                          <td className="px-3 py-2">{err.field || '—'}</td>
                          <td
                            className={`px-3 py-2 ${
                              err.severity === 'warning'
                                ? 'text-amber-700'
                                : err.severity === 'skipped'
                                  ? 'text-slate-700'
                                  : 'text-red-700'
                            }`}
                          >
                            {err.message}
                          </td>
                          <td className="px-3 py-2 capitalize">{err.severity}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <button type="button" onClick={onValidate} className="btn-secondary">
            Re-validate
          </button>
        </div>
      )}
    </div>
  );
}
